import { wallClockToIso } from '../../utils/warehouseDates'

const T = 'pending_prints.errors'

export const PRINT_JOB_ERROR_KEYS = {
  PRINT_JOB_NOT_FOUND: `${T}.PRINT_JOB_NOT_FOUND`,
  INVALID_PARAMS: `${T}.INVALID_PARAMS`,
  FORBIDDEN: `${T}.FORBIDDEN`,
  PRINT_JOB_AMBIGUOUS: `${T}.PRINT_JOB_AMBIGUOUS`,
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const DIGITS_RE = /^\d+$/
// Accepts "1864" or "#1864" (what the history shows); anything else is not an origin id.
const toOriginId = value => {
  const text = typeof value === 'string' ? value.trim().replace(/^#/, '').trim() : ''
  return DIGITS_RE.test(text) ? Number(text) : null
}

const isTransportError = error => !!error && (error.isAxiosError === true || !!error.request || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')

const getErrorCode = error => error?.response?.data?.error_code || null

export const normalizeCode = raw => {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed || null
}

export const isRouteMissing = error => error?.response?.status === 404 && !getErrorCode(error)

export const ambiguousCandidates = error => {
  if (error?.response?.status !== 422 || getErrorCode(error) !== 'PRINT_JOB_AMBIGUOUS') return null
  const candidates = error.response.data.candidates
  return Array.isArray(candidates) && candidates.length > 0 ? candidates : null
}

export const resolvePrintJobError = (error, t, { requestedCode } = {}) => {
  if (!error?.response) {
    if (error?.code === 'ECONNABORTED') return t(`${T}.timeout`)
    return t(isTransportError(error) ? `${T}.network` : `${T}.generic`)
  }
  const code = getErrorCode(error)
  if (code === 'PRINT_JOB_NOT_FOUND') return t(PRINT_JOB_ERROR_KEYS.PRINT_JOB_NOT_FOUND, { code: requestedCode ?? '' })
  const forbidden = code === 'FORBIDDEN' || (!code && error.response.status === 403)
  if (code && Object.prototype.hasOwnProperty.call(PRINT_JOB_ERROR_KEYS, code)) return t(PRINT_JOB_ERROR_KEYS[code])
  if (forbidden) return t(PRINT_JOB_ERROR_KEYS.FORBIDDEN)
  return t(`${T}.generic`)
}

// wallClockToIso only emits whole seconds, so the end of day is built at 23:59 and its seconds widened to :59.999.
export const dayRangeToInstants = ({ from, to } = {}, timeZone) => {
  if (!timeZone) return {}
  const result = {}
  if (DATE_RE.test(from)) result.last_printed_from = wallClockToIso(`${from}T00:00`, timeZone)
  if (DATE_RE.test(to)) result.last_printed_to = wallClockToIso(`${to}T23:59`, timeZone).replace(/:00(?=[+-]\d{2}:\d{2}$)/, ':59.999')
  return result
}

export const buildListParams = (searchParams = {}, filters = {}, timeZone) => {
  const { page, per_page, order_by, order } = searchParams
  const { printCode, barcode, storeName, originType, originId, kind, unprinted, lastPrinted } = filters || {}
  const params = { page, per_page }
  const normalizedPrintCode = normalizeCode(printCode)
  if (normalizedPrintCode) params.print_code = normalizedPrintCode
  const normalizedBarcode = normalizeCode(barcode)
  if (normalizedBarcode) params.barcode = normalizedBarcode
  // Partial and case-insensitive on the backend, so it is sent as typed, only trimmed.
  const normalizedStoreName = normalizeCode(storeName)
  if (normalizedStoreName) params.store_name = normalizedStoreName
  if (originType) params.origin_type = originType
  const originIdNumber = toOriginId(originId)
  if (originIdNumber !== null) params.origin_id = originIdNumber
  if (kind) params.kind = kind
  if (unprinted === true) params.printed = false
  Object.assign(params, dayRangeToInstants(lastPrinted, timeZone))
  if (order_by) params.order_by = order_by
  if (order) params.order = order
  return params
}
